import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const SchrodingerEquationCalculator = () => {
  const [potentialType, setPotentialType] = useState('harmonic');
  const [mass, setMass] = useState('9.109e-31'); // Default: electron mass
  const [potentialConstant, setPotentialConstant] = useState('1');
  const [quantumNumber, setQuantumNumber] = useState('1');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateSchrodinger = () => {
    setError('');
    
    // Constants
    const hbar = 1.0545718e-34; // Reduced Planck's constant
    
    // Validate inputs
    const m = parseFloat(mass);
    const n = parseInt(quantumNumber);
    const k = parseFloat(potentialConstant);

    if (isNaN(m) || m <= 0) {
      setError('Mass must be a positive number');
      return;
    }

    if (isNaN(n) || n <= 0) {
      setError('Quantum number must be a positive integer');
      return;
    }

    if (isNaN(k) || k <= 0) {
      setError('Potential constant must be a positive number');
      return;
    }

    // Calculate energy levels
    let energy;
    let waveFunction;
    let probabilityDensity;

    if (potentialType === 'harmonic') {
      // Harmonic oscillator
      energy = (n + 0.5) * hbar * Math.sqrt(k / m);
      
      // Generate wave function data
      const dataPoints = 100;
      const xMax = 5 * Math.sqrt(hbar / (m * k)); // ~5 standard deviations
      const deltaX = (2 * xMax) / dataPoints;
      
      waveFunction = Array.from({ length: dataPoints }, (_, i) => {
        const x = -xMax + i * deltaX;
        // Hermite polynomial for harmonic oscillator
        const hermite = hermitePolynomial(n, Math.sqrt(m * k / hbar) * x);
        const wave = Math.exp(-0.5 * m * k * x * x / hbar) * hermite;
        return { x, y: wave };
      });

      probabilityDensity = waveFunction.map(point => ({
        x: point.x,
        y: point.y * point.y
      }));
    } else {
      // Particle in a box
      const L = 1e-9; // Box length (1nm)
      energy = (n * n * Math.PI * Math.PI * hbar * hbar) / (2 * m * L * L);
      
      // Generate wave function data
      const dataPoints = 100;
      const deltaX = L / dataPoints;
      
      waveFunction = Array.from({ length: dataPoints }, (_, i) => {
        const x = i * deltaX;
        const wave = Math.sqrt(2 / L) * Math.sin(n * Math.PI * x / L);
        return { x, y: wave };
      });

      probabilityDensity = waveFunction.map(point => ({
        x: point.x,
        y: point.y * point.y
      }));
    }

    const result = {
      energy: energy.toFixed(4),
      waveFunction,
      probabilityDensity,
      potentialType
    };

    setResult(result);
    setShowGraph(true);
  };

  // Helper function for Hermite polynomials
  const hermitePolynomial = (n, x) => {
    if (n === 0) return 1;
    if (n === 1) return 2 * x;
    let h0 = 1;
    let h1 = 2 * x;
    for (let i = 2; i <= n; i++) {
      const h2 = 2 * x * h1 - 2 * (i - 1) * h0;
      h0 = h1;
      h1 = h2;
    }
    return h1;
  };

  const generateWaveFunctionGraph = () => {
    if (!result) return null;

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      xaxis: {
        title: {
          text: 'Position (m)',
        },
      },
      yaxis: {
        title: {
          text: 'Wave Function (ψ)',
        },
      },
      title: {
        text: 'Wave Function',
        align: 'center',
      },
      colors: ['#4F46E5'],
      annotations: {
        yaxis: [{
          y: 0,
          borderColor: '#E5E7EB',
          label: {
            show: true,
            text: 'Zero',
            style: {
              color: '#4B5563',
              background: '#fff',
            },
          }
        }]
      }
    };

    const series = [
      {
        name: 'Wave Function',
        data: result.waveFunction
      }
    ];

    return { series, options };
  };

  const generateProbabilityGraph = () => {
    if (!result) return null;

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      xaxis: {
        title: {
          text: 'Position (m)',
        },
      },
      yaxis: {
        title: {
          text: 'Probability Density (|ψ|^2)',
        },
      },
      title: {
        text: 'Probability Density',
        align: 'center',
      },
      colors: ['#10B981'],
      annotations: {
        yaxis: [{
          y: 0,
          borderColor: '#E5E7EB',
          label: {
            show: true,
            text: 'Zero',
            style: {
              color: '#4B5563',
              background: '#fff',
            },
          }
        }]
      }
    };

    const series = [
      {
        name: 'Probability Density',
        data: result.probabilityDensity
      }
    ];

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Schrödinger Equation Calculator | Physics Tool</title>
        <meta name="description" content="Calculate wave functions and energy levels using the Schrödinger equation. Interactive calculator for quantum systems including harmonic oscillator and particle in a box." />
        <meta name="keywords" content="schrodinger equation calculator, quantum mechanics calculator, wave function calculator, energy levels calculator, physics calculator" />
        <meta property="og:title" content="Schrödinger Equation Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate wave functions and energy levels using the Schrödinger equation. Interactive calculator for quantum systems including harmonic oscillator and particle in a box." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Schrödinger Equation Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate wave functions and energy levels using the Schrödinger equation. Interactive calculator for quantum systems including harmonic oscillator and particle in a box." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/schrodinger-equation" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-purple-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Schrödinger Equation Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Wave Functions and Energy Levels</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Potential Type</label>
                <select
                  value={potentialType}
                  onChange={(e) => setPotentialType(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="harmonic">Harmonic Oscillator</option>
                  <option value="box">Particle in a Box</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Mass (kg)</label>
                <input
                  type="number"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter mass (e.g., 9.109e-31 for electron)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Potential Constant</label>
                <input
                  type="number"
                  value={potentialConstant}
                  onChange={(e) => setPotentialConstant(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter potential constant"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Quantum Number (n)</label>
                <input
                  type="number"
                  value={quantumNumber}
                  onChange={(e) => setQuantumNumber(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter quantum number"
                />
              </div>
              <div>
                <button
                  onClick={calculateSchrodinger}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Wave Function
                </button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-100 rounded">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {result && (
                <div className="mt-4 p-4 bg-purple-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-purple-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-purple-800">Energy Level: {result.energy} J</p>
                        <p className="text-purple-800">Potential Type: {result.potentialType === 'harmonic' ? 'Harmonic Oscillator' : 'Particle in a Box'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-purple-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the Schrödinger equation for a {result.potentialType === 'harmonic' ? 'harmonic oscillator' : 'particle in a box'}:
                      <br />
                      <br />
                      For n = {quantumNumber}:
                      <br />
                      Energy = {result.energy} J
                      <br />
                      <br />
                      The wave function and probability density are shown in the graphs below.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
              <h2 className="text-2xl font-bold mb-4">Wave Function Visualization</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Wave Function (ψ)</h3>
                  <Chart
                    options={generateWaveFunctionGraph().options}
                    series={generateWaveFunctionGraph().series}
                    type="line"
                    height={400}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4">Probability Density (|ψ|^2)</h3>
                  <Chart
                    options={generateProbabilityGraph().options}
                    series={generateProbabilityGraph().series}
                    type="line"
                    height={400}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Select the potential type (Harmonic Oscillator or Particle in a Box)</li>
            <li>Enter the mass of the particle (default is electron mass)</li>
            <li>Enter the potential constant (spring constant for harmonic oscillator)</li>
            <li>Enter the quantum number (n)</li>
            <li>Click "Calculate Wave Function" to get the results</li>
            <li>View the wave function and probability density graphs</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Multiple Potential Types</h3>
              <p>Calculate solutions for both harmonic oscillator and particle in a box</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>See both wave function and probability density plots</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for students learning quantum mechanics</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Instant Feedback</h3>
              <p>Real-time validation and error messages for incorrect inputs</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is the Schrödinger Equation?</h3>
              <p className="text-gray-600">
                The Schrödinger equation is a fundamental equation in quantum mechanics that describes how the quantum state of a physical system changes over time.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What does the wave function represent?</h3>
              <p className="text-gray-600">
                The wave function (ψ) is a mathematical description of the quantum state of a system. Its square (|ψ|^2) represents the probability density of finding a particle at a given position.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are the potential types available?</h3>
              <p className="text-gray-600">
                This calculator supports two common quantum systems:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Harmonic Oscillator: A particle in a quadratic potential well</li>
                  <li>Particle in a Box: A particle confined to a finite region with infinite potential walls</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand quantum mechanics concepts</li>
                  <li>Researchers analyze quantum systems</li>
                  <li>Teachers demonstrate wave-particle duality</li>
                  <li>Anyone interested in quantum physics</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchrodingerEquationCalculator;
